import type {
  WorkflowDefinitionDiagnostic,
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
} from './platform-workflow-definition-lifecycle.models';

export interface WorkflowReviewFinding {
  readonly id: string;
  readonly diagnostic: WorkflowDefinitionDiagnostic;
  readonly sourceIndex: number;
  readonly nodeId: string | null;
}

export interface WorkflowReviewFindingGroup {
  readonly id: string;
  readonly title: string;
  readonly nodeId: string | null;
  readonly findings: readonly WorkflowReviewFinding[];
}

const workflowAreas = [
  { id: 'publication', title: 'Publication readiness' },
  { id: 'settings', title: 'Workflow settings' },
  { id: 'starting-information', title: 'Starting information' },
  { id: 'assignment', title: 'Work assignment' },
  { id: 'navigation', title: 'Workflow navigation' },
  { id: 'features', title: 'Available features' },
  { id: 'automation', title: 'Automated operations' },
  { id: 'statuses', title: 'Workflow statuses' },
  { id: 'routing', title: 'Routing & paths' },
  { id: 'results', title: 'Workflow results' },
  { id: 'steps', title: 'Work steps' },
] as const;

type WorkflowArea = (typeof workflowAreas)[number]['id'];

/** Stable across fresh response objects; an occurrence distinguishes identical findings. */
export function workflowReviewFindingId(
  diagnostic: WorkflowDefinitionDiagnostic,
  occurrence = 0,
): string {
  return `finding:${encodeURIComponent(
    JSON.stringify([
      diagnostic.code,
      diagnostic.severityCode,
      diagnostic.message,
      diagnostic.locationTypeCode,
      diagnostic.locationId,
      diagnostic.path,
    ]),
  )}:${occurrence}`;
}

/** Groups the saved server result only; local edits never resolve a finding here. */
export function workflowReviewFindingGroups(
  document: WorkflowDefinitionDocument | null,
  diagnostics: readonly WorkflowDefinitionDiagnostic[],
): WorkflowReviewFindingGroup[] {
  const sourceNodes = document?.nodes;
  const sourceEdges = document?.edges;
  const sourcePolicies = document?.assignmentPolicies;
  const nodes = Array.isArray(sourceNodes) ? sourceNodes : [];
  const edges = Array.isArray(sourceEdges) ? sourceEdges : [];
  const policies = Array.isArray(sourcePolicies) ? sourcePolicies : [];
  const step = (
    id: string | null | undefined,
  ): WorkflowDefinitionNode | null => {
    const node = uniqueIdentity(nodes, id);
    return node && ['human_step', 'automated_step'].includes(node.typeCode)
      ? node
      : null;
  };
  const owner = (
    diagnostic: WorkflowDefinitionDiagnostic,
  ): WorkflowDefinitionNode | null => {
    const { path, locationId, locationTypeCode } = diagnostic;
    if (rootArea(path, 'nodes')) {
      const node =
        path === '$.nodes' && locationTypeCode === 'node'
          ? uniqueIdentity(nodes, locationId)
          : locateRoot(nodes, 'nodes', path);
      if (
        !node ||
        ['edge', 'assignment_policy'].includes(locationTypeCode) ||
        (locationTypeCode === 'node' &&
          locationId !== null &&
          locationId !== node.id) ||
        !nestedLocationAgrees(diagnostic, node)
      )
        return null;
      return step(node.id);
    }
    if (rootArea(path, 'edges')) {
      const edge =
        path === '$.edges' && locationTypeCode === 'edge'
          ? uniqueIdentity(edges, locationId)
          : locateRoot(edges, 'edges', path);
      return edge &&
        !['node', 'assignment_policy'].includes(locationTypeCode) &&
        (locationTypeCode !== 'edge' ||
          locationId === null ||
          locationId === edge.id)
        ? step(edge.fromNodeId)
        : null;
    }
    if (rootArea(path, 'assignmentPolicies')) {
      const policy =
        path === '$.assignmentPolicies' &&
        locationTypeCode === 'assignment_policy'
          ? uniqueIdentity(policies, locationId)
          : locateRoot(policies, 'assignmentPolicies', path);
      if (
        !policy ||
        policy.scopeCode !== 'step' ||
        ['node', 'edge'].includes(locationTypeCode) ||
        (locationTypeCode === 'assignment_policy' &&
          locationId !== null &&
          locationId !== policy.id) ||
        policies.filter((candidate) => candidate?.nodeId === policy.nodeId)
          .length !== 1
      )
        return null;
      return step(policy.nodeId);
    }
    // A missing path may still carry an exact node location; a different root
    // path is a conflicting locator and must retain its workflow-area grouping.
    return (!path || path === '$') && locationTypeCode === 'node'
      ? step(locationId)
      : null;
  };
  const occurrences = new Map<string, number>();
  const grouped = new Map<string, WorkflowReviewFinding[]>();
  const stepNodes = new Map<string, WorkflowDefinitionNode>();
  diagnostics.forEach((diagnostic, sourceIndex) => {
    const base = workflowReviewFindingId(diagnostic);
    const occurrence = occurrences.get(base) ?? 0;
    occurrences.set(base, occurrence + 1);
    const node = owner(diagnostic);
    const groupId = node
      ? `step:${encodeURIComponent(node.id)}`
      : `workflow:${workflowArea(diagnostic)}`;
    if (node) stepNodes.set(groupId, node);
    const finding: WorkflowReviewFinding = {
      id: workflowReviewFindingId(diagnostic, occurrence),
      diagnostic,
      sourceIndex,
      nodeId: node?.id ?? null,
    };
    const findings = grouped.get(groupId) ?? [];
    findings.push(finding);
    grouped.set(groupId, findings);
  });
  const priority = (finding: WorkflowReviewFinding) =>
    finding.diagnostic.severityCode === 'error'
      ? 0
      : finding.diagnostic.severityCode === 'warning'
        ? 1
        : 2;
  grouped.forEach((findings) =>
    findings.sort(
      (left, right) =>
        priority(left) - priority(right) ||
        left.sourceIndex - right.sourceIndex,
    ),
  );
  const workflowGroups: WorkflowReviewFindingGroup[] = workflowAreas.flatMap(
    (area) => {
      const id = `workflow:${area.id}`;
      const findings = grouped.get(id);
      return findings
        ? [{ id, title: area.title, nodeId: null, findings }]
        : [];
    },
  );
  const steps: WorkflowReviewFindingGroup[] = [...stepNodes.entries()]
    .sort(([, left], [, right]) => {
      const order = (node: WorkflowDefinitionNode) =>
        Number.isFinite(node.displayOrder)
          ? node.displayOrder
          : Number.POSITIVE_INFINITY;
      return (
        order(left) - order(right) || nodes.indexOf(left) - nodes.indexOf(right)
      );
    })
    .map(([id, node]) => ({
      id,
      title: text(node.displayName) || text(node.stepCode) || 'Unnamed step',
      nodeId: node.id,
      findings: grouped.get(id)!,
    }));
  return [...workflowGroups, ...steps];
}

/** Navigates this result snapshot once, without treating local edits as fixes. */
export function nextWorkflowReviewFinding(
  groups: readonly WorkflowReviewFindingGroup[],
  currentId: string | null,
  isActionable: (finding: WorkflowReviewFinding) => boolean = () => true,
): WorkflowReviewFinding | null {
  const findings = groups.flatMap((group) => group.findings);
  const currentIndex =
    currentId === null
      ? -1
      : findings.findIndex((finding) => finding.id === currentId);
  if (currentId !== null && currentIndex < 0) return null;
  const available = currentId === null ? findings.length : findings.length - 1;
  for (let offset = 1; offset <= available; offset++) {
    const finding = findings[(currentIndex + offset) % findings.length];
    if (isActionable(finding)) return finding;
  }
  return null;
}

function uniqueIdentity<T extends { id: string }>(
  items: readonly (T | null)[],
  id: string | null | undefined,
): T | null {
  if (typeof id !== 'string' || !id || id.trim() !== id) return null;
  const matches = items.filter((item) => item?.id === id);
  return matches.length === 1 ? matches[0] : null;
}

function rootArea(path: string, collection: string): boolean {
  const prefix = `$.${collection}`;
  return (
    path === prefix ||
    path.startsWith(`${prefix}[`) ||
    path.startsWith(`${prefix}.`)
  );
}

function locateRoot<T extends { id: string }>(
  items: readonly (T | null)[],
  collection: string,
  path: string,
): T | null {
  const match = new RegExp(
    `^\\$\\.${collection}\\[(?:id=([^\\]]+)|(0|[1-9]\\d*))\\](?=\\.|$)`,
  ).exec(path);
  if (!match) return null;
  if (match[1]) return uniqueIdentity(items, match[1]);
  const index = Number(match[2]);
  if (!Number.isSafeInteger(index)) return null;
  const candidate = items[index];
  return candidate ? uniqueIdentity(items, candidate.id) : null;
}

function nestedLocationAgrees(
  diagnostic: WorkflowDefinitionDiagnostic,
  node: WorkflowDefinitionNode,
): boolean {
  if (
    !['timer', 'child'].includes(diagnostic.locationTypeCode) ||
    diagnostic.locationId === null
  )
    return true;
  if (
    diagnostic.path.endsWith('.terminalOutcome') ||
    diagnostic.path.includes('.terminalOutcome.')
  )
    return diagnostic.locationId === node.id;
  const collection =
    diagnostic.locationTypeCode === 'timer' ? 'timers' : 'childLinks';
  const leaf = new RegExp(
    `\\.${collection}(\\[(?:id=([^\\]]+)|(0|[1-9]\\d*))\\])?(?=\\.|$)`,
  ).exec(diagnostic.path);
  if (!leaf) return false;
  if (!leaf[1]) return diagnostic.locationId === node.id;
  const source = node.declaration?.behavior[collection];
  const items: readonly ({ id: string } | null)[] = Array.isArray(source)
    ? source.map((item: unknown) =>
        item !== null &&
        typeof item === 'object' &&
        !Array.isArray(item) &&
        'id' in item &&
        typeof item.id === 'string'
          ? { id: item.id }
          : null,
      )
    : [];
  const item = leaf[2]
    ? uniqueIdentity(items, leaf[2])
    : items[Number(leaf[3])];
  return (
    !!item &&
    uniqueIdentity(items, item.id) !== null &&
    diagnostic.locationId === item.id
  );
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function workflowArea(diagnostic: WorkflowDefinitionDiagnostic): WorkflowArea {
  const path = diagnostic.path;
  if (
    [
      '$.environment',
      '$.configurationCode',
      '$.configurationVersionCode',
    ].includes(path)
  )
    return 'publication';
  if (rootArea(path, 'processData')) return 'starting-information';
  if (
    rootArea(path, 'assignmentPolicies') ||
    diagnostic.locationTypeCode === 'assignment_policy'
  )
    return 'assignment';
  if (rootArea(path, 'navigation')) return 'navigation';
  if (rootArea(path, 'capabilityReferences') || rootArea(path, 'packReference'))
    return 'features';
  if (rootArea(path, 'triggers')) return 'automation';
  if (rootArea(path, 'statusConfiguration')) return 'statuses';
  if (
    rootArea(path, 'edges') ||
    rootArea(path, 'workflowTransitions') ||
    rootArea(path, 'requiredDecisions') ||
    diagnostic.locationTypeCode === 'edge'
  )
    return 'routing';
  if (rootArea(path, 'nodes'))
    return path.includes('.terminalOutcome') ? 'results' : 'steps';
  return 'settings';
}
